import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  alpha,
  useTheme,
  Skeleton,
  Fade,
  Tooltip,
  IconButton,
  Divider
} from '@mui/material';
import {
  Warning,
  Assignment,
  Search,
  CheckCircle,
  TrendingUp,
  Timer,
  Speed,
  Refresh,
  ArrowForward,
  ErrorOutline
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

interface DashboardMetrics {
  total: number;
  open: number;
  assigned: number;
  investigating: number;
  resolved: number;
  closed: number;
  sla_breaches: number;
  avg_resolution_time_hours: number;
  sla_compliance_rate: number;
}

const IncidentDashboard: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const {
    data: metrics,
    isLoading,
    error,
    refetch,
    isRefetching
  } = useQuery<DashboardMetrics>({
    queryKey: ['incidentDashboard'],
    queryFn: async () => {
      const response = await axios.get('/api/incidents/dashboard');
      return response.data.data;
    },
    refetchInterval: 30000 // Auto-refresh every 30 seconds
  });

  // Enhanced loading state with skeletons
  if (isLoading) {
    return (
      <Box>
        <Box mb={4}>
          <Skeleton variant="text" width={400} height={48} />
          <Skeleton variant="text" width={600} height={24} />
        </Box>
        <Grid container spacing={3}>
          {[1, 2, 3, 4, 5, 6, 7].map((i) => (
            <Grid item xs={12} sm={6} md={i <= 4 ? 3 : 4} key={i}>
              <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  // Enhanced error state
  if (error) {
    return (
      <Fade in>
        <Box>
          <Alert
            severity="error"
            icon={<ErrorOutline />}
            sx={{
              borderRadius: 2,
              boxShadow: theme.shadows[2]
            }}
            action={
              <IconButton color="inherit" size="small" onClick={() => refetch()}>
                <Refresh />
              </IconButton>
            }
          >
            <Typography variant="subtitle2" fontWeight={600}>
              Failed to load dashboard metrics
            </Typography>
            <Typography variant="body2">
              Unable to retrieve incident data. Please try again or contact support if the issue
              persists.
            </Typography>
          </Alert>
        </Box>
      </Fade>
    );
  }

  // Enhanced MetricCard with modern design
  const MetricCard = ({
    title,
    value,
    icon,
    color,
    subtitle,
    onClick
  }: {
    title: string;
    value: number | string;
    icon: React.ReactNode;
    color: string;
    subtitle?: string;
    onClick?: () => void;
  }) => (
    <Fade in timeout={600}>
      <Card
        elevation={0}
        sx={{
          height: '100%',
          cursor: onClick ? 'pointer' : 'default',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`,
          '&:hover': onClick
            ? {
                transform: 'translateY(-4px)',
                boxShadow: theme.shadows[8],
                borderColor: alpha(color, 0.3)
              }
            : {}
        }}
        onClick={onClick}
      >
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
            <Box flex={1}>
              <Typography
                variant="body2"
                color="text.secondary"
                fontWeight={500}
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontSize: '0.75rem',
                  mb: 1
                }}
              >
                {title}
              </Typography>
              <Typography
                variant="h3"
                fontWeight={700}
                sx={{
                  background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.7)} 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 0.5
                }}
              >
                {value}
              </Typography>
              {subtitle && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                  {subtitle}
                </Typography>
              )}
            </Box>
            <Box
              sx={{
                background: `linear-gradient(135deg, ${alpha(color, 0.15)} 0%, ${alpha(color, 0.05)} 100%)`,
                borderRadius: 2.5,
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {icon}
            </Box>
          </Box>
          {onClick && (
            <Box
              display="flex"
              alignItems="center"
              gap={0.5}
              sx={{
                color: alpha(color, 0.8),
                fontSize: '0.875rem',
                fontWeight: 500,
                mt: 1
              }}
            >
              View Details
              <ArrowForward sx={{ fontSize: 16 }} />
            </Box>
          )}
        </CardContent>
      </Card>
    </Fade>
  );

  // Calculate active incidents
  const activeIncidents =
    (metrics?.open || 0) + (metrics?.assigned || 0) + (metrics?.investigating || 0);
  const slaComplianceColor =
    (metrics?.sla_compliance_rate || 0) >= 90
      ? '#4caf50'
      : (metrics?.sla_compliance_rate || 0) >= 70
        ? '#ff9800'
        : '#f44336';

  return (
    <Box>
      {/* Enhanced Header */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={4}
        sx={{
          pb: 3,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
        }}
      >
        <Box>
          <Typography
            variant="h4"
            fontWeight={700}
            sx={{
              mb: 0.5,
              background: `linear-gradient(135deg, ${theme.palette.text.primary} 0%, ${alpha(theme.palette.text.primary, 0.7)} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            Incident Management Dashboard
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            Real-time monitoring and incident response tracking
            {isRefetching && <CircularProgress size={14} thickness={6} />}
          </Typography>
        </Box>
        <Tooltip title="Refresh Dashboard">
          <IconButton
            onClick={() => refetch()}
            disabled={isRefetching}
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.2)
              }
            }}
          >
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      <Grid container spacing={3}>
        {/* Critical Status Metrics - Top Row */}
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Open Incidents"
            value={metrics?.open || 0}
            icon={<Warning sx={{ fontSize: 32, color: '#f44336' }} />}
            color="#f44336"
            subtitle="Requires attention"
            onClick={() => navigate('/incidents?status=open')}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Assigned"
            value={metrics?.assigned || 0}
            icon={<Assignment sx={{ fontSize: 32, color: '#ff9800' }} />}
            color="#ff9800"
            subtitle="In progress"
            onClick={() => navigate('/incidents?status=assigned')}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Investigating"
            value={metrics?.investigating || 0}
            icon={<Search sx={{ fontSize: 32, color: '#2196f3' }} />}
            color="#2196f3"
            subtitle="Under review"
            onClick={() => navigate('/incidents?status=investigating')}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Resolved"
            value={metrics?.resolved || 0}
            icon={<CheckCircle sx={{ fontSize: 32, color: '#4caf50' }} />}
            color="#4caf50"
            subtitle="Completed"
            onClick={() => navigate('/incidents?status=resolved')}
          />
        </Grid>

        {/* Performance Metrics - Second Row */}
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            title="Total Incidents"
            value={metrics?.total || 0}
            icon={<TrendingUp sx={{ fontSize: 32, color: '#9c27b0' }} />}
            color="#9c27b0"
            subtitle={`${activeIncidents} active incidents`}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            title="Avg Resolution Time"
            value={
              metrics?.avg_resolution_time_hours && metrics.avg_resolution_time_hours > 0
                ? `${Math.abs(metrics.avg_resolution_time_hours)}h`
                : '0h'
            }
            icon={<Timer sx={{ fontSize: 32, color: '#00bcd4' }} />}
            color="#00bcd4"
            subtitle="Mean time to resolve"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            title="SLA Compliance"
            value={`${metrics?.sla_compliance_rate || 0}%`}
            icon={<Speed sx={{ fontSize: 32, color: slaComplianceColor }} />}
            color={slaComplianceColor}
            subtitle={`${metrics?.sla_breaches || 0} breach${(metrics?.sla_breaches || 0) !== 1 ? 'es' : ''}`}
          />
        </Grid>

        {/* SLA Breaches Alert - Enhanced */}
        {metrics && metrics.sla_breaches > 0 && (
          <Grid item xs={12}>
            <Fade in>
              <Alert
                severity="warning"
                icon={<Warning />}
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${alpha('#ff9800', 0.3)}`,
                  bgcolor: alpha('#ff9800', 0.05),
                  '& .MuiAlert-icon': {
                    color: '#ff9800'
                  }
                }}
                action={
                  <Chip
                    label="View Breaches"
                    size="small"
                    onClick={() => navigate('/incidents?sla_breach=true')}
                    icon={<ArrowForward sx={{ fontSize: 16 }} />}
                    sx={{
                      cursor: 'pointer',
                      fontWeight: 600,
                      bgcolor: alpha('#ff9800', 0.15),
                      '&:hover': {
                        bgcolor: alpha('#ff9800', 0.25)
                      }
                    }}
                  />
                }
              >
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  SLA Breach Alert
                </Typography>
                <Typography variant="body2">
                  <strong>{metrics.sla_breaches}</strong> incident
                  {metrics.sla_breaches !== 1 ? 's have' : ' has'} breached SLA deadlines and
                  require immediate attention.
                </Typography>
              </Alert>
            </Fade>
          </Grid>
        )}

        {/* Quick Actions - Enhanced */}
        <Grid item xs={12}>
          <Fade in timeout={800}>
            <Card
              elevation={0}
              sx={{
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Typography
                    variant="h6"
                    fontWeight={600}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    Quick Actions
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box display="flex" gap={1.5} flexWrap="wrap">
                  <Chip
                    label="View All Incidents"
                    icon={<TrendingUp />}
                    onClick={() => navigate('/incidents')}
                    clickable
                    color="primary"
                    variant="outlined"
                    sx={{
                      fontWeight: 500,
                      px: 1,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.1)
                      }
                    }}
                  />
                  <Chip
                    label="My Assigned Incidents"
                    icon={<Assignment />}
                    onClick={() => navigate('/incidents?assigned_to_me=true')}
                    clickable
                    color="secondary"
                    variant="outlined"
                    sx={{
                      fontWeight: 500,
                      px: 1,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.secondary.main, 0.1)
                      }
                    }}
                  />
                  <Chip
                    label="Critical Incidents"
                    icon={<Warning />}
                    onClick={() => navigate('/incidents?severity=critical')}
                    clickable
                    color="error"
                    variant="outlined"
                    sx={{
                      fontWeight: 500,
                      px: 1,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.error.main, 0.1)
                      }
                    }}
                  />
                  <Chip
                    label="SLA Breaches"
                    icon={<Timer />}
                    onClick={() => navigate('/incidents?sla_breach=true')}
                    clickable
                    color="warning"
                    variant="outlined"
                    sx={{
                      fontWeight: 500,
                      px: 1,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.warning.main, 0.1)
                      }
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Fade>
        </Grid>
      </Grid>
    </Box>
  );
};

export default IncidentDashboard;
